import { useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Mounts its children once they come near the screen, and keeps them mounted from then on: a card may hold unsaved typing, so it
 * must never unmount again just because it scrolled away. Until then the placeholder stands in. Without IntersectionObserver
 * (old browsers, tests) everything mounts at once.
 */
export function LazyMount({ active, placeholder, children }: { active: boolean; placeholder: ReactNode; children: ReactNode }) {
  const [shown, setShown] = useState(() => !active || typeof IntersectionObserver === "undefined");
  const ref = useRef<HTMLDivElement>(null);
  // Mounted once for any reason (it was the current step), it stays: becoming lazy again later must not unmount it.
  if (!active && !shown) setShown(true);
  useEffect(() => {
    if (shown || !active) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: "800px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shown, active]);
  if (shown || !active) return <>{children}</>; // `!active` covers the render before the state above lands
  return <div ref={ref}>{placeholder}</div>;
}
